import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getApiUrl } from '../../../config/api';
import { getCurrentUserRole, getCurrentUser } from '../../../utils/permissions';
import { getLocalConfigsList } from '../../../utils/configUtils';
import { getCachedConfigFlags, loadConfigFlags } from '../../../utils/configFlagCache';
import { pluralConfigs } from '../../../utils/plural';
import FlagIcon from '../../FlagIcon/FlagIcon';
import s from './SapsTab.module.sass';
import { confirmDialog } from '../../../utils/confirmDialog';

// Роли: подпись + фирменный цвет для бейджей и пилюль
const ROLE_META = {
    admin:    { label: 'Админ', color: '#F06A6A' },
    teamlead: { label: 'Тимлид саппов', color: '#B48CF2' },
    trusted:  { label: 'Доверенный', color: '#3ECF8E' },
    sap:      { label: 'Сапп', color: '#4DA3FF' },
};

const roleStyle = (role) => {
    const m = ROLE_META[role] || ROLE_META.sap;
    return { color: m.color, background: `${m.color}1F`, borderColor: `${m.color}55` };
};

const configLabel = (config) => {
    const filename = config.filename || config.name || config;
    return config.name || String(filename).replace('.json', '');
};

const configMatches = (config, query) =>
    !query.trim() || configLabel(config).toLowerCase().includes(query.trim().toLowerCase());

const SapsTab = () => {
    const [saps, setSaps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ username: '', password: '', role: 'sap', allowedConfigs: [] });
    const [showPassword, setShowPassword] = useState(false);
    const [selectedConfigFilters, setSelectedConfigFilters] = useState([]);
    const [showConfigFilterDropdown, setShowConfigFilterDropdown] = useState(false);
    const [configFilterSearch, setConfigFilterSearch] = useState('');
    const [configModalSearch, setConfigModalSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRoleFilters, setSelectedRoleFilters] = useState([]);
    const [showRoleFilterDropdown, setShowRoleFilterDropdown] = useState(false);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [availableConfigs, setAvailableConfigs] = useState([]);
    const [configFlags, setConfigFlags] = useState({});
    const getAuthHeaders = () => {
        const token = localStorage.getItem('authToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const roles = [
        { value: 'admin', label: 'Админ' },
        { value: 'teamlead', label: 'Тимлид саппов' },
        { value: 'trusted', label: 'Доверенный' },
        { value: 'sap', label: 'Сапп' }
    ];

    useEffect(() => {
        loadCurrentUser();
        loadSaps();
        loadAvailableConfigs();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest(`.${s.dropdownWrapper}`)) {
                setShowConfigFilterDropdown(false);
                setConfigFilterSearch('');
                setShowRoleFilterDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, []);

    const loadAvailableConfigs = async () => {
        try {
            const configs = await getLocalConfigsList();
            setAvailableConfigs(configs);
            // Флаги — из общего кэша: мгновенно при повторных заходах,
            // одним обновлением (а не по одному) при первой загрузке
            setConfigFlags(getCachedConfigFlags());
            loadConfigFlags().then(setConfigFlags);
        } catch (err) {
            console.error('Ошибка загрузки конфигов:', err);
            setAvailableConfigs([]);
        }
    };

    const loadCurrentUser = async () => {
        const currentUser = getCurrentUser();
        if (currentUser) {
            setCurrentUserId(currentUser.id);
            const role = await getCurrentUserRole();
            setCurrentUserRole(role);
        }
    };

    const loadSaps = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch('/api/files/saps', {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                const sapsList = Array.isArray(data.saps) ? data.saps : [];
                // Сортируем по дате создания: старые сверху (по возрастанию createdAt)
                const sortedSaps = sapsList.sort((a, b) => {
                    const dateA = new Date(a.createdAt || 0).getTime();
                    const dateB = new Date(b.createdAt || 0).getTime();
                    return dateA - dateB; // По возрастанию (старые сверху)
                });
                setSaps(sortedSaps);
            } else {
                setSaps([]);
            }
        } catch (err) {
            console.error('Ошибка загрузки сапов:', err);
            setSaps([]);
        } finally {
            setLoading(false);
        }
    };

    const saveSapsToFile = async (sapsList) => {
        try {
            console.log('[SAVE] Отправка на сервер:', sapsList.length, 'сапов');
            console.log('[SAVE] Данные:', sapsList.map(s => ({ id: s.id, username: s.username })));
            
            const response = await fetch(getApiUrl('api/files/saps'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ saps: sapsList || [] })
            });
            
            console.log('[SAVE] Ответ сервера:', response.status, response.statusText);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('[SAVE] Ошибка HTTP:', errorText);
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }
            
            const result = await response.json();
            console.log('[SAVE] Результат:', result);
            
            if (!result.success) {
                throw new Error(result?.error || 'Ошибка сохранения файла');
            }
            
            return { success: true };
        } catch (error) {
            console.error('[SAVE] Ошибка сохранения на сервер:', error);
            throw new Error(`Не удалось сохранить на сервер: ${error.message}`);
        }
    };

    const handleOpenModal = () => {
        setFormData({ username: '', password: '', role: 'sap', allowedConfigs: [] });
        setEditingId(null);
        setShowPassword(false);
        setConfigModalSearch('');
        setError(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFormData({ username: '', password: '', role: 'sap', allowedConfigs: [] });
        setEditingId(null);
        setShowPassword(false);
        setConfigModalSearch('');
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.username.trim() || !formData.password.trim()) {
            setError('Заполните все поля');
            return;
        }

        // Тимлид сапов не может создавать админов
        if (currentUserRole === 'teamlead' && formData.role === 'admin') {
            setError('Тимлид саппов не может создавать администраторов');
            return;
        }

        try {
            setError(null);
            let updatedSaps = [...saps];

            if (editingId) {
                // Проверяем, не пытается ли тимлид редактировать админа
                const editingSap = updatedSaps.find(s => s.id === editingId);
                if (currentUserRole === 'teamlead' && editingSap?.role === 'admin') {
                    setError('Тимлид саппов не может редактировать администраторов');
                    return;
                }

                // Обновление существующего сапа
                const index = updatedSaps.findIndex(s => s.id === editingId);
                if (index !== -1) {
                    // Тимлид не может изменить роль на admin
                    const newRole = (currentUserRole === 'teamlead' && formData.role === 'admin') 
                        ? editingSap.role 
                        : formData.role;
                    
                    updatedSaps[index] = {
                        ...updatedSaps[index],
                        username: formData.username.trim(),
                        password: formData.password.trim(),
                        role: newRole,
                        allowedConfigs: newRole === 'admin' ? [] : (formData.allowedConfigs || []),
                        updatedAt: new Date().toISOString()
                    };
                }
            } else {
                // Создание нового сапа
                const newSap = {
                    id: Date.now() + Math.random(),
                    username: formData.username.trim(),
                    password: formData.password.trim(),
                    role: formData.role || 'sap',
                    allowedConfigs: formData.role === 'admin' ? [] : (formData.allowedConfigs || []),
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
                updatedSaps.push(newSap);
            }

            await saveSapsToFile(updatedSaps);
            await loadSaps();
            handleCloseModal();
        } catch (err) {
            console.error('Ошибка сохранения сапа:', err);
            setError('Не удалось сохранить: ' + err.message);
        }
    };

    const handleEdit = (sap) => {
        // Тимлид сапов не может редактировать админов
        if (currentUserRole === 'teamlead' && sap.role === 'admin') {
            setError('Тимлид саппов не может редактировать администраторов');
            return;
        }
        setFormData({ 
            username: sap.username, 
            password: sap.password, 
            role: sap.role || 'sap',
            allowedConfigs: sap.allowedConfigs || []
        });
        setEditingId(sap.id);
        setShowPassword(true);
        setConfigModalSearch('');
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        console.log('[DELETE] Начало удаления, ID:', id, 'Тип:', typeof id);
        console.log('[DELETE] Текущие сапы:', saps.map(s => ({ id: s.id, idType: typeof s.id, username: s.username })));
        
        const sapToDelete = saps.find(s => {
            // Сравниваем ID как строки для надежности
            const sapId = String(s.id || '');
            const targetId = String(id || '');
            const matches = sapId === targetId;
            if (matches) {
                console.log('[DELETE] Найден сап для удаления:', s);
            }
            return matches;
        });
        
        if (!sapToDelete) {
            console.error('[DELETE] Сап не найден! ID:', id);
            setError('Пользователь не найден');
            return;
        }
        
        // Тимлид сапов не может удалять админов
        if (currentUserRole === 'teamlead' && sapToDelete?.role === 'admin') {
            setError('Тимлид саппов не может удалять администраторов');
            return;
        }

        if (!(await confirmDialog({
            title: 'Удалить сотрудника?',
            message: 'Учётная запись будет удалена, сотрудник потеряет доступ к панели.',
            confirmText: 'Удалить',
            cancelText: 'Оставить',
            confirmHint: 'Удалит учётную запись и доступ сотрудника',
            cancelHint: 'Закроет окно, доступ сохранится'
        }))) {
            return;
        }

        try {
            setError(null);
            // Фильтруем с правильным сравнением ID
            const updatedSaps = saps.filter(s => {
                const sapId = String(s.id || '');
                const targetId = String(id || '');
                const shouldKeep = sapId !== targetId;
                if (!shouldKeep) {
                    console.log('[DELETE] Удаляем сап:', s);
                }
                return shouldKeep;
            });
            
            console.log('[DELETE] Было сапов:', saps.length);
            console.log('[DELETE] Станет сапов:', updatedSaps.length);
            console.log('[DELETE] Отправляем на сервер:', updatedSaps.map(s => ({ id: s.id, username: s.username })));
            
            await saveSapsToFile(updatedSaps);
            console.log('[DELETE] Успешно сохранено на сервер');
            await loadSaps();
            console.log('[DELETE] Список обновлен');
        } catch (err) {
            console.error('[DELETE] Ошибка удаления сапа:', err);
            setError('Не удалось удалить: ' + err.message);
        }
    };

    const handleConfigToggle = (configFilename) => {
        const currentConfigs = formData.allowedConfigs || [];
        if (currentConfigs.includes(configFilename)) {
            setFormData({
                ...formData,
                allowedConfigs: currentConfigs.filter(c => c !== configFilename)
            });
        } else {
            setFormData({
                ...formData,
                allowedConfigs: [...currentConfigs, configFilename]
            });
        }
    };

    const filteredSaps = saps.filter(sap => {
        // Тимлид сапов не видит админов и себя самого
        if (currentUserRole === 'teamlead') {
            if (sap.role === 'admin') {
                return false;
            }
            if (sap.id === currentUserId) {
                return false;
            }
        }

        if (selectedConfigFilters.length > 0) {
            if (sap.role === 'admin') {
                return false;
            }
            const allowedConfigs = sap.allowedConfigs || [];
            const hasAnySelectedConfig = selectedConfigFilters.some((selectedConfig) => allowedConfigs.includes(selectedConfig));
            if (!hasAnySelectedConfig) {
                return false;
            }
        }

        if (selectedRoleFilters.length > 0 && !selectedRoleFilters.includes(sap.role || 'sap')) {
            return false;
        }

        if (searchQuery.trim() && !(sap.username || '').toLowerCase().includes(searchQuery.trim().toLowerCase())) {
            return false;
        }

        return true;
    });
    const availableRoles = roles.filter(role => !(currentUserRole === 'teamlead' && role.value === 'admin'));
    const editingSap = editingId ? saps.find(x => String(x.id) === String(editingId)) : null;
    const modalConfigs = availableConfigs.filter(c => configMatches(c, configModalSearch));

    if (loading) {
        return (
            <div className={s.loading}>
                <div className={s.spinner}></div>
                <p>Загрузка пользователей...</p>
            </div>
        );
    }

    return (
        <div className={s.sapsTab}>
            {error && (
                <div className={s.errorBanner}>
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className={s.errorClose}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
            )}

            <section className={s.panel}>
                <header className={s.panelHead}>
                    <div className={s.panelHeadMain}>
                        <span className={s.panelIcon}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v2"></path>
                                <circle cx="8" cy="7" r="4"></circle>
                                <line x1="20" y1="8" x2="20" y2="14"></line>
                                <line x1="23" y1="11" x2="17" y2="11"></line>
                            </svg>
                        </span>
                        <div className={s.panelHeadText}>
                            <h2 className={s.panelTitle}>Управление саппами</h2>
                            <p className={s.panelSub}>Создавайте саппов, назначайте роли и доступные конфиги</p>
                        </div>
                    </div>
                    <div className={s.panelHeadSide}>
                        <span className={s.statChip}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v2"></path>
                                <circle cx="8" cy="7" r="4"></circle>
                            </svg>
                            <span>Всего: <strong>{filteredSaps.length}</strong></span>
                        </span>
                        <button onClick={handleOpenModal} className={s.addButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            <span>Добавить саппа</span>
                        </button>
                    </div>
                </header>

                <div className={s.controls}>
                    <div className={s.searchContainer}>
                        <svg className={s.searchIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input
                            type="text"
                            className={s.searchInput}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Поиск по логину..."
                        />
                        {searchQuery && (
                            <button type="button" className={s.searchClear} onClick={() => setSearchQuery('')} title="Очистить поиск">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        )}
                    </div>
                    <div className={s.dropdownWrapper}>
                        <button
                            type="button"
                            className={`${s.dropdownButton} ${selectedConfigFilters.length > 0 ? s.dropdownActive : ''}`}
                            title="Фильтр по конфигу"
                            onClick={() => {
                                setShowRoleFilterDropdown(false);
                                setShowConfigFilterDropdown(!showConfigFilterDropdown);
                            }}
                        >
                            <svg className={s.ddIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            <span>
                                {selectedConfigFilters.length === 0
                                    ? 'Все конфиги'
                                    : selectedConfigFilters.length === 1
                                        ? (availableConfigs.find(c => (c.filename || c.name || c) === selectedConfigFilters[0])?.name
                                            || selectedConfigFilters[0].replace('.json', ''))
                                        : `Конфигов: ${selectedConfigFilters.length}`
                                }
                            </span>
                            <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points={showConfigFilterDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                            </svg>
                        </button>
                        {showConfigFilterDropdown && (
                            <div className={s.dropdownMenu}>
                                <div className={s.dropdownHeader}>
                                    <span>Выберите конфиги</span>
                                    {selectedConfigFilters.length > 0 && (
                                        <button
                                            type="button"
                                            className={s.clearAllButton}
                                            onClick={() => setSelectedConfigFilters([])}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                            <span>Очистить</span>
                                        </button>
                                    )}
                                </div>
                                <div className={s.menuSearch}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    </svg>
                                    <input
                                        type="text"
                                        value={configFilterSearch}
                                        onChange={(e) => setConfigFilterSearch(e.target.value)}
                                        placeholder="Поиск конфига..."
                                        autoFocus
                                    />
                                    {configFilterSearch && (
                                        <button type="button" className={s.menuSearchClear} onClick={() => setConfigFilterSearch('')}>
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                <div className={s.checkboxList}>
                                    {availableConfigs.filter(c => configMatches(c, configFilterSearch)).length === 0 ? (
                                        <p className={s.noResults}>Ничего не найдено</p>
                                    ) : (
                                        availableConfigs.filter(c => configMatches(c, configFilterSearch)).map((config) => {
                                            const configFilename = config.filename || config.name || config;
                                            const configName = configLabel(config);
                                            const isChecked = selectedConfigFilters.includes(configFilename);
                                            return (
                                                <label key={configFilename} className={s.checkboxItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => {
                                                            setSelectedConfigFilters((prev) => (
                                                                prev.includes(configFilename)
                                                                    ? prev.filter((name) => name !== configFilename)
                                                                : [...prev, configFilename]
                                                        ));
                                                    }}
                                                />
                                                <span className={s.configOption}>
                                                    <FlagIcon code={configFlags[configFilename] || 'unknown'} className={s.flagMini} alt="" />
                                                    <span>{configName}</span>
                                                </span>
                                            </label>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={s.dropdownWrapper}>
                        <button
                            type="button"
                            className={`${s.dropdownButton} ${selectedRoleFilters.length > 0 ? s.dropdownActive : ''}`}
                            title="Фильтр по роли"
                            onClick={() => {
                                setShowConfigFilterDropdown(false);
                                setConfigFilterSearch('');
                                setShowRoleFilterDropdown(!showRoleFilterDropdown);
                            }}
                        >
                            <svg className={s.ddIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 7h-9"></path>
                                <path d="M14 17H5"></path>
                                <circle cx="17" cy="17" r="3"></circle>
                                <circle cx="7" cy="7" r="3"></circle>
                            </svg>
                            <span>
                                {selectedRoleFilters.length === 0
                                    ? 'Все роли'
                                    : selectedRoleFilters.length === 1
                                        ? (ROLE_META[selectedRoleFilters[0]] || ROLE_META.sap).label
                                        : `Ролей: ${selectedRoleFilters.length}`
                                }
                            </span>
                            <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points={showRoleFilterDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                            </svg>
                        </button>
                        {showRoleFilterDropdown && (
                            <div className={s.dropdownMenu}>
                                <div className={s.dropdownHeader}>
                                    <span>Выберите роли</span>
                                    {selectedRoleFilters.length > 0 && (
                                        <button
                                            type="button"
                                            className={s.clearAllButton}
                                            onClick={() => setSelectedRoleFilters([])}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                            <span>Очистить</span>
                                        </button>
                                    )}
                                </div>
                                <div className={s.checkboxList}>
                                    {availableRoles.map((role) => {
                                        const isChecked = selectedRoleFilters.includes(role.value);
                                        return (
                                            <label key={role.value} className={s.checkboxItem}>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => {
                                                        setSelectedRoleFilters((prev) => (
                                                            prev.includes(role.value)
                                                                ? prev.filter((v) => v !== role.value)
                                                                : [...prev, role.value]
                                                        ));
                                                    }}
                                                />
                                                <span className={s.roleOption}>
                                                    <span className={s.roleDot} style={{ background: (ROLE_META[role.value] || ROLE_META.sap).color }} />
                                                    <span>{role.label}</span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    <button onClick={loadSaps} className={s.refreshButton} title="Обновить список">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="23 4 23 10 17 10"></polyline>
                            <polyline points="1 20 1 14 7 14"></polyline>
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                        </svg>
                        <span>Обновить</span>
                    </button>
                </div>

                <div className={s.panelBody}>
                {filteredSaps.length === 0 ? (
                    <div className={s.empty}>
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={s.emptyIcon}>
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <p>Саппы не созданы</p>
                        <button onClick={handleOpenModal} className={s.emptyAddButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            <span>Создать первого саппа</span>
                        </button>
                    </div>
                ) : (
                    <div className={s.sapsGrid}>
                        {filteredSaps.map((sap) => {
                            const isAdmin = sap.role === 'admin';
                            const canEdit = !(currentUserRole === 'teamlead' && isAdmin);
                            const canDelete = !(currentUserRole === 'teamlead' && isAdmin);
                            const initial = (sap.username || 'U').trim()[0]?.toUpperCase() || 'U';
                            const created = sap.createdAt ? new Date(sap.createdAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
                            const cfgCount = (sap.allowedConfigs || []).length;

                            return (
                                <div key={sap.id} className={s.sapCard}>
                                    <div className={s.cardHeader}>
                                        <div className={s.userAvatar} style={roleStyle(sap.role || 'sap')}>{initial}</div>
                                        <div className={s.headMain}>
                                            <h3 className={s.userName} title={sap.username}>{sap.username}</h3>
                                            <div className={s.headChips}>
                                                <span className={s.roleChip} style={roleStyle(sap.role || 'sap')}>
                                                    <span className={s.roleDot} style={{ background: (ROLE_META[sap.role] || ROLE_META.sap).color }} />
                                                    {(ROLE_META[sap.role] || ROLE_META.sap).label}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={s.headActions}>
                                            <button
                                                onClick={() => handleEdit(sap)}
                                                className={s.iconEdit}
                                                disabled={!canEdit}
                                                title={!canEdit ? 'Недостаточно прав' : 'Редактировать'}
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                </svg>
                                            </button>
                                            <button
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(sap.id); }}
                                                className={s.iconDelete}
                                                disabled={!canDelete}
                                                title={!canDelete ? 'Недостаточно прав' : 'Удалить'}
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div className={s.cardBody}>
                                        <div className={s.metaStrip}>
                                            <span className={s.metaChip} title="Пароль скрыт">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="11" width="18" height="10" rx="2"></rect>
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                                </svg>
                                                <span>••••••</span>
                                            </span>
                                            <span className={s.metaChip} title="Дата создания">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                                </svg>
                                                <span>{created}</span>
                                            </span>
                                            {isAdmin ? (
                                                <span className={`${s.metaChip} ${s.metaChipFull}`} title="Доступ ко всем конфигам">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                                                    </svg>
                                                    <span>Полный доступ</span>
                                                </span>
                                            ) : cfgCount > 0 ? (
                                                <span
                                                    className={s.metaChip}
                                                    title={(sap.allowedConfigs || []).map(fn => String(fn).replace('.json', '')).join('\n')}
                                                >
                                                    <span className={s.flagStack}>
                                                        {/* Одна страна — один флаг: дубли схлопываем, число конфигов — в тексте */}
                                                        {[...new Set((sap.allowedConfigs || []).map(fn => configFlags[fn] || 'unknown'))].slice(0, 4).map(code => (
                                                            <FlagIcon key={code} code={code} className={s.flagMini} alt="" />
                                                        ))}
                                                    </span>
                                                    <span>{pluralConfigs(cfgCount)}</span>
                                                </span>
                                            ) : (
                                                <span className={s.metaChip} title="Доступные конфиги">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                        <polyline points="7 10 12 15 17 10"></polyline>
                                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                                    </svg>
                                                    <span>Конфиги не выбраны</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                </div>
            </section>

            {/* Модальное окно */}
            {isModalOpen && ReactDOM.createPortal(
                <div
                    className={s.modalOverlay}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}
                >
                    <div className={s.modalContent} onMouseDown={(e) => e.stopPropagation()}>
                        <div className={s.modalHeader}>
                            <div className={s.modalHeaderText}>
                                <h3 className={s.modalTitle}>
                                    <span className={s.modalTitleIcon}>
                                        {editingId ? (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                        ) : (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M16 21v-2a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v2"></path>
                                                <circle cx="8" cy="7" r="4"></circle>
                                                <line x1="20" y1="8" x2="20" y2="14"></line>
                                                <line x1="23" y1="11" x2="17" y2="11"></line>
                                            </svg>
                                        )}
                                    </span>
                                    <span>{editingId ? 'Редактировать саппа' : 'Новый сапп'}</span>
                                </h3>
                                <p className={s.modalSubtitle}>Учётные данные, роль и доступ к конфигам</p>
                            </div>
                            <button className={s.modalClose} onClick={handleCloseModal}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className={s.modalForm}>
                            {error && <div className={s.errorMessage}>{error}</div>}

                            {editingSap && (
                                <div className={s.editSummary}>
                                    <div className={s.editAvatar} style={roleStyle(formData.role || 'sap')}>
                                        {(formData.username || editingSap.username || 'U').trim()[0]?.toUpperCase() || 'U'}
                                    </div>
                                    <div className={s.editSummaryMain}>
                                        <span className={s.editSummaryName}>{formData.username || editingSap.username}</span>
                                        <div className={s.editSummaryChips}>
                                            <span className={s.roleChip} style={roleStyle(formData.role || 'sap')}>
                                                <span className={s.roleDot} style={{ background: (ROLE_META[formData.role] || ROLE_META.sap).color }} />
                                                {(ROLE_META[formData.role] || ROLE_META.sap).label}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={s.editSummaryMeta}>
                                        {editingSap.createdAt && (
                                            <span className={s.editMetaChip} title="Дата создания">
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                                </svg>
                                                <span>Создан {new Date(editingSap.createdAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                            </span>
                                        )}
                                        {editingSap.updatedAt && (
                                            <span className={s.editMetaChip} title="Последнее изменение">
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <polyline points="12 6 12 12 16 14"></polyline>
                                                </svg>
                                                <span>Изменён {new Date(editingSap.updatedAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className={s.formSection}>
                                <div className={s.sectionLabel}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="7" r="4"></circle>
                                        <path d="M5.5 21a6.5 6.5 0 0 1 13 0"></path>
                                    </svg>
                                    <span>Учётные данные</span>
                                </div>
                                <div className={s.credentialsRow}>
                                    <div className={s.formGroup}>
                                        <label className={s.label}>Логин</label>
                                        <input
                                            type="text"
                                            className={s.input}
                                            value={formData.username}
                                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                            placeholder="Введите логин"
                                            required
                                            autoFocus
                                        />
                                    </div>
                                    <div className={s.formGroup}>
                                        <label className={s.label}>Пароль</label>
                                        <div className={s.passwordContainer}>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                className={s.input}
                                                value={formData.password}
                                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                placeholder="Введите пароль"
                                                required
                                            />
                                            <button
                                                type="button"
                                                className={s.togglePassword}
                                                onClick={() => setShowPassword(!showPassword)}
                                                title={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                            >
                                                {showPassword ? (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                    </svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={s.formSection}>
                                <div className={s.sectionLabel}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 7h-9"></path>
                                        <path d="M14 17H5"></path>
                                        <circle cx="17" cy="17" r="3"></circle>
                                        <circle cx="7" cy="7" r="3"></circle>
                                    </svg>
                                    <span>Роль</span>
                                </div>
                                <div className={s.rolePills}>
                                    {availableRoles.map((role) => {
                                        const active = formData.role === role.value;
                                        return (
                                            <button
                                                key={role.value}
                                                type="button"
                                                className={`${s.rolePill} ${active ? s.rolePillActive : ''}`}
                                                style={active ? roleStyle(role.value) : undefined}
                                                onClick={() => {
                                                    setFormData({
                                                        ...formData,
                                                        role: role.value,
                                                        allowedConfigs: role.value === 'admin' ? [] : formData.allowedConfigs
                                                    });
                                                }}
                                            >
                                                <span className={s.roleDot} style={{ background: (ROLE_META[role.value] || ROLE_META.sap).color }} />
                                                <span>{role.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {formData.role === 'admin' && (
                                    <p className={s.roleHint}>Админ имеет доступ ко всем конфигам без ограничений</p>
                                )}
                            </div>

                            {formData.role !== 'admin' && (
                                <div className={s.formSection}>
                                    <div className={s.sectionLabel}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                        <span>Доступные конфиги</span>
                                        {(formData.allowedConfigs || []).length > 0 && (
                                            <span className={s.sectionCounter}>{formData.allowedConfigs.length}</span>
                                        )}
                                    </div>
                                    <div className={s.configsSearch}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="11" cy="11" r="8"></circle>
                                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                        </svg>
                                        <input
                                            type="text"
                                            value={configModalSearch}
                                            onChange={(e) => setConfigModalSearch(e.target.value)}
                                            placeholder="Поиск конфига..."
                                        />
                                        {configModalSearch && (
                                            <button type="button" className={s.menuSearchClear} onClick={() => setConfigModalSearch('')}>
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    <div className={s.configsBox}>
                                        {availableConfigs.length === 0 ? (
                                            <p className={s.noConfigs}>Конфиги не найдены</p>
                                        ) : modalConfigs.length === 0 ? (
                                            <p className={s.noConfigs}>Ничего не найдено по запросу</p>
                                        ) : (
                                            modalConfigs.map((config) => {
                                                const configFilename = config.filename || config;
                                                const isChecked = (formData.allowedConfigs || []).includes(configFilename);
                                                return (
                                                    <label key={configFilename} className={s.checkboxItem}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => handleConfigToggle(configFilename)}
                                                        />
                                                        <span className={s.configOption}>
                                                            <FlagIcon code={configFlags[configFilename] || 'unknown'} className={s.flagMini} alt="" />
                                                            <span>{config.name || configFilename.replace('.json', '')}</span>
                                                        </span>
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>
                                    {formData.allowedConfigs && formData.allowedConfigs.length === 0 && (
                                        <p className={s.configWarning}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                            </svg>
                                            <span>Без выбранного конфига сапп не сможет выбрать конфиг в админке</span>
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className={s.modalActions}>
                                <button type="button" onClick={handleCloseModal} className={s.cancelButton}>
                                    <span>Отмена</span>
                                </button>
                                <button type="submit" className={s.submitButton}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                        <polyline points="7 3 7 8 15 8"></polyline>
                                    </svg>
                                    <span>{editingId ? 'Сохранить изменения' : 'Создать саппа'}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default SapsTab;
